import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api';

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('stats');
  const [loading, setLoading] = useState(true);
  const [revealedIds, setRevealedIds] = useState({});

  if (user.role !== 'admin') return <Navigate to="/" />;

  const fetchData = async () => { // eslint-disable-line
    try {
      const [statsRes, usersRes, auditRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/audit-log')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setAuditLogs(auditRes.data.logs);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const handleSearch = async () => {
    const { data } = await api.get(`/admin/users?search=${encodeURIComponent(search)}`);
    setUsers(data.users);
  };

  const revealIdentity = async (userId) => {
    const { data } = await api.get(`/admin/users/${userId}/identity`);
    setRevealedIds(prev => ({ ...prev, [userId]: data.user }));
    // Refresh audit log
    const auditRes = await api.get('/admin/audit-log');
    setAuditLogs(auditRes.data.logs);
  };

  const changeRole = async (userId, newRole) => {
    const label = newRole === 'moderator' ? 'Promote to moderator' : newRole === 'admin' ? 'Promote to admin' : 'Demote to user';
    if (!window.confirm(`${label}?`)) return;
    await api.put(`/admin/users/${userId}/role`, { role: newRole });
    fetchData();
  };

  const tabStyle = (active) => ({
    fontSize: 14, padding: '8px 16px', borderRadius: 9999, cursor: 'pointer',
    fontFamily: '"Geist", "Inter", system-ui, sans-serif', textTransform: 'capitalize',
    border: active ? '1px solid #e8b547' : '1px solid rgba(237,228,211,0.08)',
    background: active ? '#e8b547' : '#120c09',
    color: active ? '#0e0a08' : 'rgba(237, 228, 211, 0.38)',
  });

  return (
    <div style={{ minHeight: '100vh', paddingBottom: 120, background: '#0e0a08' }}>
      <Navbar />

      <div style={{ borderBottom: '1px solid rgba(237,228,211,0.08)', background: 'rgba(18,12,9,0.3)' }}>
        <div style={{ maxWidth: 768, margin: '0 auto', padding: '20px 22px' }}>
          <h2 style={{ fontFamily: '"Instrument Serif", serif', fontSize: 18, color: '#ede4d3', margin: 0 }}>Admin Dashboard</h2>
          <p style={{ color: 'rgba(237, 228, 211, 0.25)', fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif', marginTop: 4 }}>Platform management. All identity access is audited.</p>
        </div>
      </div>

      <main style={{ maxWidth: 768, margin: '0 auto', padding: '24px 22px' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {['stats', 'users', 'audit'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={tabStyle(tab === t)}>
              {t === 'audit' ? 'Audit Log' : t}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ color: '#e8b547', fontFamily: '"Instrument Serif", serif' }}>Loading...</p>
          </div>
        ) : tab === 'stats' && stats ? (
          <>
            {/* Stats grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
              {[
                ['Users', stats.totals.users],
                ['Posts', stats.totals.posts],
                ['Debriefs', stats.totals.debriefs],
                ['Check-Ins', stats.totals.checkins],
                ['Chat Rooms', stats.totals.chatrooms],
                ['Mod Actions', stats.totals.modActions],
              ].map(([label, val]) => (
                <div key={label} style={{ background: '#120c09', border: '1px solid rgba(237,228,211,0.08)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                  <p style={{ fontFamily: '"Instrument Serif", serif', fontSize: 24, color: '#e8b547', margin: 0 }}>{val}</p>
                  <p style={{ color: 'rgba(237, 228, 211, 0.25)', fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif', marginTop: 4 }}>{label}</p>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ background: '#120c09', border: '1px solid rgba(237,228,211,0.08)', borderRadius: 12, padding: 16 }}>
                <h4 style={{ color: 'rgba(237, 228, 211, 0.38)', fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif', marginBottom: 8, marginTop: 0 }}>By Role</h4>
                {Object.entries(stats.usersByRole).map(([role, count]) => (
                  <div key={role} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontFamily: '"Geist", "Inter", system-ui, sans-serif', padding: '4px 0' }}>
                    <span style={{ color: 'rgba(237, 228, 211, 0.5)', textTransform: 'capitalize' }}>{role}</span>
                    <span style={{ color: '#e8b547' }}>{count}</span>
                  </div>
                ))}
              </div>
              <div style={{ background: '#120c09', border: '1px solid rgba(237,228,211,0.08)', borderRadius: 12, padding: 16 }}>
                <h4 style={{ color: 'rgba(237, 228, 211, 0.38)', fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif', marginBottom: 8, marginTop: 0 }}>By Status</h4>
                {Object.entries(stats.usersByStatus).map(([status, count]) => (
                  <div key={status} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontFamily: '"Geist", "Inter", system-ui, sans-serif', padding: '4px 0' }}>
                    <span style={{ color: 'rgba(237, 228, 211, 0.5)', textTransform: 'capitalize' }}>{status}</span>
                    <span style={{ color: '#e8b547' }}>{count}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontFamily: '"Geist", "Inter", system-ui, sans-serif', padding: '4px 0', marginTop: 8, borderTop: '1px solid rgba(237,228,211,0.05)' }}>
                  <span style={{ color: 'rgba(237, 228, 211, 0.5)' }}>Signups (7d)</span>
                  <span style={{ color: '#e8b547' }}>{stats.recentSignups}</span>
                </div>
              </div>
            </div>
          </>
        ) : tab === 'users' ? (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search by alias, email, or name..."
                style={{
                  flex: 1, background: '#0e0a08', border: '1px solid rgba(237,228,211,0.08)',
                  borderRadius: 12, padding: '10px 16px', color: '#ede4d3', fontSize: 14,
                  fontFamily: '"Geist", "Inter", system-ui, sans-serif', outline: 'none', boxSizing: 'border-box',
                }} />
              <button onClick={handleSearch}
                style={{
                  background: '#e8b547', color: '#0e0a08', fontFamily: '"Geist", "Inter", system-ui, sans-serif',
                  fontWeight: 600, fontSize: 14, padding: '10px 20px', borderRadius: 12,
                  border: 'none', cursor: 'pointer',
                }}>
                Search
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {users.map(u => {
                const revealed = revealedIds[u._id];
                return (
                  <div key={u._id} style={{ background: '#120c09', border: '1px solid rgba(237,228,211,0.08)', borderRadius: 12, padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: '"Instrument Serif", serif', fontSize: 14, color: '#e8b547' }}>{u.alias}</span>
                          <span style={{ color: 'rgba(237, 228, 211, 0.15)', fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif' }}>{u.department}</span>
                          <span style={{
                            fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif',
                            padding: '2px 8px', borderRadius: 9999,
                            background: u.role === 'admin' ? 'rgba(192,132,252,0.12)' : u.role === 'moderator' ? 'rgba(96,165,250,0.12)' : 'rgba(237,228,211,0.04)',
                            color: u.role === 'admin' ? '#c084fc' : u.role === 'moderator' ? '#60a5fa' : 'rgba(237, 228, 211, 0.2)',
                          }}>{u.role}</span>
                          {u.status !== 'active' && (
                            <span style={{ fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif', color: '#d47e6a' }}>{u.status}</span>
                          )}
                          {u.warnings?.length > 0 && (
                            <span style={{ fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif', color: '#e8b547' }}>{u.warnings.length}w</span>
                          )}
                        </div>

                        {revealed && (
                          <div style={{ marginTop: 8, background: 'rgba(14,10,8,0.5)', borderRadius: 8, padding: '8px 12px' }}>
                            <p style={{ color: '#ede4d3', fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif', margin: '2px 0' }}><strong>Name:</strong> {revealed.name}</p>
                            <p style={{ color: '#ede4d3', fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif', margin: '2px 0' }}><strong>Email:</strong> {revealed.email}</p>
                          </div>
                        )}

                        <p style={{ color: 'rgba(237, 228, 211, 0.15)', fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif', marginTop: 4 }}>Joined {timeAgo(u.createdAt)}</p>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                        {!revealed && (
                          <button onClick={() => revealIdentity(u._id)}
                            style={{ fontSize: 12, color: 'rgba(237, 228, 211, 0.2)', fontFamily: '"Geist", "Inter", system-ui, sans-serif', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
                            Reveal ID
                          </button>
                        )}
                        {u.role === 'user' && (
                          <button onClick={() => changeRole(u._id, 'moderator')}
                            style={{ fontSize: 12, color: 'rgba(96,165,250,0.6)', fontFamily: '"Geist", "Inter", system-ui, sans-serif', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
                            Make mod
                          </button>
                        )}
                        {u.role === 'moderator' && (
                          <button onClick={() => changeRole(u._id, 'user')}
                            style={{ fontSize: 12, color: 'rgba(237, 228, 211, 0.2)', fontFamily: '"Geist", "Inter", system-ui, sans-serif', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
                            Remove mod
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : tab === 'audit' ? (
          auditLogs.length === 0 ? (
            <div style={{ background: 'rgba(18,12,9,0.3)', border: '1px dashed rgba(237,228,211,0.08)', borderRadius: 16, padding: 40, textAlign: 'center' }}>
              <p style={{ color: 'rgba(237, 228, 211, 0.2)', fontSize: 14, fontFamily: '"Geist", "Inter", system-ui, sans-serif' }}>No audit entries yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {auditLogs.map(log => (
                <div key={log._id} style={{ background: '#120c09', border: '1px solid rgba(237,228,211,0.08)', borderRadius: 12, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif', fontWeight: 500, color: '#c084fc' }}>{log.action.replace(/_/g, ' ')}</span>
                        <span style={{ fontSize: 12, color: 'rgba(232,181,71,0.6)', fontFamily: '"Geist", "Inter", system-ui, sans-serif' }}>{log.targetAlias}</span>
                        <span style={{ fontSize: 12, color: 'rgba(237, 228, 211, 0.15)', fontFamily: '"Geist", "Inter", system-ui, sans-serif' }}>by {log.adminAlias}</span>
                      </div>
                      {log.details && <p style={{ fontSize: 12, color: 'rgba(237, 228, 211, 0.2)', fontFamily: '"Geist", "Inter", system-ui, sans-serif', marginTop: 4 }}>{log.details}</p>}
                    </div>
                    <span style={{ fontSize: 12, color: 'rgba(237, 228, 211, 0.15)', fontFamily: '"Geist", "Inter", system-ui, sans-serif', whiteSpace: 'nowrap' }}>{timeAgo(log.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : null}
      </main>
    </div>
  );
}
